/**
 * One place for "give the user a file". The server generates the content; this only delivers it.
 *  - Desktop (Electron): the preload bridge opens a native Save dialog and writes the file.
 *  - Browser: downloads through a temporary link.
 * Web code never imports Electron; the bridge is optional and feature-detected.
 */
export interface DesktopBridge {
  saveFile(req: { apiPath: string; suggestedName: string }): Promise<{ saved: boolean }>;
  /** Where the recipes live: serverUrl is null when they are stored on this PC. */
  getConnection(): Promise<{ serverUrl: string | null }>;
  openConnection(): Promise<void>;
  /** Server mode only: the automatic local copy of the server's data. */
  getLocalBackup(): Promise<{
    enabled: boolean;
    dir: string;
    keep: number;
    count: number;
    last: { at: string; status: 'saved' | 'unchanged' } | null;
  }>;
  openBackupFolder(): Promise<void>;
}

declare global {
  interface Window {
    muddleDesktop?: DesktopBridge;
  }
}

/** `apiPath` is relative to /api, e.g. "/export/xlsx". Resolves { saved: false } if the user cancels. */
export async function saveFromApi(
  apiPath: string,
  suggestedName: string,
): Promise<{ saved: boolean }> {
  if (window.muddleDesktop) {
    return window.muddleDesktop.saveFile({ apiPath: `/api${apiPath}`, suggestedName });
  }
  const res = await fetch(`/api${apiPath}`);
  if (!res.ok) {
    let message = `Export failed (${res.status})`;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { saved: true };
}

export const today = (): string => new Date().toISOString().slice(0, 10);
